/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { useState, useContext } from 'react';
import validate from 'validate.js'
import { IndexContext } from '../IndexProvider';
import PAGES from '../consts/pages';

const usernameConstraints = {
    username: {
        presence: {
            allowEmpty: false
        },
        email: true
    }
};

const passwordConstraints = {
    password: {
        presence: {
            allowEmpty: false
        }
    }
};

export default function Login() {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("")
    const [isUsernameValid, setIsUsernameValid] = useState(false)
    const [isPasswordValid, setIsPasswordValid] = useState(false)
    const [shouldShowUsernameFeedback, setShouldShowUsernameFeedback] = useState(false)
    const [shouldShowPasswordFeedback, setShouldShowPasswordFeedback] = useState(false)
    const [isPending, setIsPending] = useState(false)

    const { login, pageLoading, setCurrentPage } = useContext(IndexContext)

    const onSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        let count = 0

        if (validate({ username: username }, usernameConstraints)) {
            setIsUsernameValid(false)
        } else {
            setIsUsernameValid(true)
            count++
        }

        if (validate({ password: password }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
            count++
        }

        if (count === 2) {
            setIsPending(true)
            await login(username, password)
            setIsPending(false)
        }
    };

    const onUsernameChange = (event) => {
        setUsername(event.target.value)
        setShouldShowUsernameFeedback(true)
        if (validate({ username: event.target.value }, usernameConstraints)) {
            setIsUsernameValid(false)
        } else {
            setIsUsernameValid(true)
        }
    }

    const onPasswordChange = (event) => {
        setPassword(event.target.value)
        setShouldShowPasswordFeedback(true)
        if (validate({ password: event.target.value }, passwordConstraints)) {
            setIsPasswordValid(false)
        } else {
            setIsPasswordValid(true)
        }
    }

    const buildFormControlClasses = (shouldShowFeedback, isValid) => {
        if (shouldShowFeedback) {
            return `form-control ${isValid ? "is-valid" : "is-invalid"}`
        } else {
            return "form-control"
        }
    }

    const onForgetPasswordClick = (e) => {
        e.preventDefault()
        setCurrentPage(PAGES.RESET_PASSWORD_GENERATE_CODE)
    }

    const createMainElement = (isSkeleton) => {
        const firstDiv =
            <div className={"mb-3 d-flex justify-content-center align-items-center" + (isSkeleton ? " skeleton-1" : "")} style={{ backgroundColor: "#0073e6", fontSize: 24, fontWeight: "bold", color: "#fff" }}>
                <label style={{ marginBottom: 0, visibility: isSkeleton ? "hidden" : "visible" }}>ログイン</label>
            </div>

        const secondDiv =
            <div className={"mb-3" + (isSkeleton ? " skeleton-1" : "")} >
                <label htmlFor="emailInput" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>メールアドレス</label>
                {
                    isSkeleton ? <input className="form-control" style={{ visibility: "hidden" }} id="emailInput" /> : <input className={buildFormControlClasses(shouldShowUsernameFeedback, isUsernameValid)} onChange={onUsernameChange} value={username} type="email" id="emailInput" required />
                }
                <div className="invalid-feedback">
                    有効なメールアドレスを入力してください。
                </div>
            </div>

        const thirdDiv =
            <div className={"mb-3" + (isSkeleton ? " skeleton-1" : "")} >
                <label htmlFor="passwordInput" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>パスワード</label>
                {
                    isSkeleton ? <input className="form-control" style={{ visibility: "hidden" }} id="passwordInput" /> : <input className={buildFormControlClasses(shouldShowPasswordFeedback, isPasswordValid)} onChange={onPasswordChange} value={password} type="password" id="passwordInput" required />
                }
                <div className="invalid-feedback">
                    パスワードを入力してください。
                </div>
            </div>

        const fourthDiv =
            <div className={"d-flex align-items-center justify-content-between" + (isSkeleton ? " skeleton-1" : "")} >
                {
                    isSkeleton ?
                        <>
                            <button className="btn btn-primary" style={{ visibility: "hidden" }} >skeleton</button>
                            <a style={{ visibility: "hidden" }} >skeleton</a>
                        </>
                        :
                        <>
                            <button className="btn btn-primary" type="submit" >
                                {
                                    isPending ? "ログイン中..." : "ログイン"
                                }
                            </button>
                            <a style={{ cursor: "pointer", fontSize: 12 }} onClick={onForgetPasswordClick} >パスワードを忘れた場合 </a>
                        </>
                }
            </div>

        return (
            <>
                {firstDiv}
                {secondDiv}
                {thirdDiv}
                {fourthDiv}
            </>
        )
    }

    return (
        pageLoading ?
            <>
                <div className="d-flex justify-content-center align-items-center ps-3 pe-3 pt-5 pb-5">
                    <div className="w-100 form-container p-3 " style={{ border: "1px solid #fff", backgroundColor: "#fbfafa" }} >
                        {createMainElement(true)}
                    </div>
                </div>
            </>
            :
            <>
                <div className="d-flex justify-content-center align-items-center ps-3 pe-3 pt-5 pb-5">
                    <form noValidate className="w-100 form-container p-3" style={{ border: "1px solid #0073e6" }} onSubmit={onSubmit} >
                        {createMainElement()}
                    </form>
                </div>
            </>
    );
}